import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardMedia, CardActions,
  IconButton, Button, LinearProgress, Alert, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../AuthContext';
import { getHouse } from '../api';

function SortablePhoto({ img, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.id });

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card
        ref={setNodeRef}
        sx={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #2a2a2a',
          borderRadius: 2,
          overflow: 'hidden',
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.5 : 1,
          cursor: isDragging ? 'grabbing' : 'default',
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height="160"
            image={img.url}
            alt={img.filename}
            sx={{ objectFit: 'cover', display: 'block' }}
          />
          {/* Drag handle */}
          <Box
            {...attributes}
            {...listeners}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: 1,
              p: '2px 4px',
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              color: '#ccc',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)', color: '#fff' },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        </Box>
        <CardActions sx={{ justifyContent: 'space-between', px: 1.5, py: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ maxWidth: '75%' }}
          >
            {img.filename}
          </Typography>
          <Tooltip title="Remove photo">
            <IconButton size="small" color="error" onClick={() => onDelete(img.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    </Grid>
  );
}

function PhotoManager({ houseId }) {
  const { authHeaders } = useAuth();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadImages = () => {
    getHouse(houseId).then(h => setImages(h.images || []));
  };

  useEffect(() => { loadImages(); }, [houseId]);

  const handleUpload = async e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const res = await fetch(`/api/houses/${houseId}/photos`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    setUploading(false);
    if (res.ok) {
      loadImages();
      fileRef.current.value = '';
    } else {
      setError('Upload failed — please try again.');
    }
  };

  const handleDelete = async (imgId) => {
    if (!window.confirm('Remove this photo?')) return;
    await fetch(`/api/photos/${imgId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    loadImages();
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex(i => i.id === active.id);
    const newIndex = images.findIndex(i => i.id === over.id);
    const reordered = arrayMove(images, oldIndex, newIndex);
    setImages(reordered);
    await fetch(`/api/houses/${houseId}/photos/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ photo_ids: reordered.map(i => i.id) }),
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Photos</Typography>
          <Typography variant="caption" color="text.secondary">
            Drag to reorder · first photo is the cover
          </Typography>
        </Box>
        <Button
          variant="outlined"
          component="label"
          disabled={uploading}
          size="small"
          sx={{ borderColor: '#444', color: '#ccc', '&:hover': { borderColor: '#FFA13C', color: '#FFA13C' } }}
        >
          {uploading ? 'Uploading...' : '+ Upload'}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleUpload}
          />
        </Button>
      </Box>

      {uploading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {images.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed #333',
            borderRadius: 2,
            py: 6,
            textAlign: 'center',
          }}
        >
          <Typography color="text.disabled" variant="body2" mb={1}>No photos yet</Typography>
          <Button
            variant="text"
            component="label"
            size="small"
            sx={{ color: '#FFA13C' }}
          >
            Upload first photo
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleUpload}
            />
          </Button>
        </Box>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={images.map(i => i.id)} strategy={rectSortingStrategy}>
            <Grid container spacing={2}>
              {images.map(img => (
                <SortablePhoto key={img.id} img={img} onDelete={handleDelete} />
              ))}
            </Grid>
          </SortableContext>
        </DndContext>
      )}
    </Box>
  );
}

export default PhotoManager;
